import { PRODUCT_SERVICE_NAME } from '#client/pages/Home/constants';

import { globalEntitlements } from '../utils/globalEntitlements';

const DOMAIN_MESSAGE_PREFIX = 'productPage.domainNameReg.';
const DOMAIN_MESSAGE_SQSP_PREFIX = 'productPage.domainNameReg.sqsp.';

/**
 * Maps a domain registration page message id to the Squarespace-reseller tree when applicable.
 * @param {string} id
 * @returns {string}
 */
export function toSqspDomainMessageId(id) {
  if (typeof id !== 'string') {
    return id;
  }
  if (id.startsWith(DOMAIN_MESSAGE_SQSP_PREFIX)) {
    return id;
  }
  if (id.startsWith(DOMAIN_MESSAGE_PREFIX)) {
    return `${DOMAIN_MESSAGE_PREFIX}sqsp.${id.slice(DOMAIN_MESSAGE_PREFIX.length)}`;
  }
  return id;
}

/**
 * Deep-clones JSON-serializable config and rewrites domain page message ids to the sqsp tree.
 * Rebuilds getBuyButtonConfig so CTA copy uses the sqsp message key (structuredClone cannot copy functions).
 * @param {object} base
 * @returns {object}
 */
export function withSqspMessageKeys(base) {
  const serializable = { ...base };
  delete serializable.getBuyButtonConfig;
  const cloned = JSON.parse(JSON.stringify(serializable));

  const mapValue = (value) => {
    if (typeof value === 'string') {
      return toSqspDomainMessageId(value);
    }
    if (Array.isArray(value)) {
      return value.map(mapValue);
    }
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapValue(v)]));
    }
    return value;
  };

  const mapped = mapValue(cloned);

  // Use custom service to:
  // 1. not show price in buybox
  // 2. make cta url point to domain search page
  mapped.hero.buyBox[0].interactService.service = 'domain_name_reg_sqsp';

  // Add partner logo to the buybox subheader
  mapped.hero.partnerLogo = {
    src: '/s2/vector/squarespace-logo.svg',
    alt: `${DOMAIN_MESSAGE_SQSP_PREFIX}heroPartnerLogo.altText`,
  };

  // Add partner review to buybox subheader
  mapped.hero.partnerReviewBlanks = [
    {
      text: `${DOMAIN_MESSAGE_SQSP_PREFIX}partnerReviewBlanks.trustpilot.reviewText`,
    },
    {
      src: '/illustration/trustpilot-4.5-star.svg',
      showOnMobile: true,
      alt: `${DOMAIN_MESSAGE_SQSP_PREFIX}partnerReviewBlanks.trustpilot.reviewStarAltText`,
    },
    {
      src: '/illustration/trustpilot-logo.svg',
      alt: `${DOMAIN_MESSAGE_SQSP_PREFIX}partnerReviewBlanks.trustpilot.reviewLogoAltText`,
    },
  ];

  // Change benefit icons
  mapped.benefit.items[0].icon = '/s2/material-icons/managesearchrounded.svg';
  mapped.benefit.items[1].icon = '/s2/material-icons/gppgoodrounded.svg';
  mapped.benefit.items[2].icon = '/s2/material-icons/accounttreerounded.svg';

  // Change highlight icon
  mapped.highlight.icon = '/s2/material-icons/languagerounded.svg';

  return {
    ...mapped,
    getBuyButtonConfig: globalEntitlements({
      ctaText: toSqspDomainMessageId('productPage.domainNameReg.buyBox.buttonLabel'),
    })
      .allowMultiplePurchases()
      .build(),
  };
}

export const DOMAIN_NAME_REG_CONFIG = {
  hero: {
    buyBox: [
      {
        bulletPoints: [
          { title: 'productPage.domainNameReg.buyBox.bulletPoint1' },
          { title: 'productPage.domainNameReg.buyBox.bulletPoint2' },
          { title: 'productPage.domainNameReg.buyBox.bulletPoint3' },
        ],
        productName: 'productPage.domainNameReg.buyBox.productName',
        interactService: {
          service: 'domain_name_reg',
        },
      },
    ],
    headerPicture: {
      src: '/s2/raster/smiling-cafe-coffee.png',
      alt: 'productPage.domainNameReg.heroPicture.altText',
    },
    headline: 'productPage.domainNameReg.page.headline',
    partnerLogo: {},
    partnerReviewBlanks: [],
    subheader: 'productPage.domainNameReg.page.subheader',
  },
  benefit: {
    title: 'productPage.domainNameReg.valueProp.title',
    items: [
      {
        icon: '/s2/material-icons/gppgoodrounded.svg',
        title: 'productPage.domainNameReg.valueProp.propTitle1',
        description: 'productPage.domainNameReg.valueProp.shortCopy1',
      },
      {
        icon: '/s2/material-icons/computerrounded.svg',
        title: 'productPage.domainNameReg.valueProp.propTitle2',
        description: 'productPage.domainNameReg.valueProp.shortCopy2',
      },
      {
        icon: '/s2/material-icons/campaignrounded.svg',
        title: 'productPage.domainNameReg.valueProp.propTitle3',
        description: 'productPage.domainNameReg.valueProp.shortCopy3',
      },
    ],
  },
  highlight: {
    icon: '/s2/material-icons/helpoutlinerounded.svg',
    title: 'productPage.domainNameReg.topFeature.title',
    subheader: 'productPage.domainNameReg.topFeature.subheader',
    image: '/s2/raster/laptop-kitchen-white.png',
    alt: 'productPage.domainNameReg.topFeature.altText',
  },
  howItWorks: {
    title: 'productPage.domainNameReg.howItWorks.title',
    items: [
      {
        title: 'productPage.domainNameReg.howItWorks.step1',
        description: 'productPage.domainNameReg.howItWorks.shortDescription1',
      },
      {
        title: 'productPage.domainNameReg.howItWorks.step2',
        description: 'productPage.domainNameReg.howItWorks.shortDescription2',
      },
      {
        title: 'productPage.domainNameReg.howItWorks.step3',
        description: 'productPage.domainNameReg.howItWorks.shortDescription3',
      },
    ],
    image: '/s2/raster/outside-phone-smiling.png',
    alt: 'productPage.domainNameReg.howItWorks.altText',
    hasNumber: true,
    hasDivider: false,
    hasMobileImage: true,
  },
  faqs: {
    title: 'productPage.domainNameReg.frequentlyAskedQuestions.title',
    items: [
      {
        question: 'productPage.domainNameReg.frequentlyAskedQuestions.faq1',
        answer: 'productPage.domainNameReg.frequentlyAskedQuestions.faqAnswer1',
      },
      {
        question: 'productPage.domainNameReg.frequentlyAskedQuestions.faq2',
        answer: 'productPage.domainNameReg.frequentlyAskedQuestions.faqAnswer2',
      },
      {
        question: 'productPage.domainNameReg.frequentlyAskedQuestions.faq3',
        answer: 'productPage.domainNameReg.frequentlyAskedQuestions.faqAnswer3',
      },
      {
        question: 'productPage.domainNameReg.frequentlyAskedQuestions.faq4',
        answer: 'productPage.domainNameReg.frequentlyAskedQuestions.faqAnswer4',
      },
    ],
  },
  assistance: {
    title: 'productPage.domainNameReg.needAssistance.title',
    getInTouch: 'productPage.domainNameReg.needAssistance.getInTouch',
    actions: 'productPage.domainNameReg.needAssistance.actions',
  },
  getBuyButtonConfig: globalEntitlements({
    ctaText: 'productPage.domainNameReg.buyBox.buttonLabel',
  })
    .allowMultiplePurchases()
    .build(),
};

/**
 * Returns domain registration landing config; uses the Squarespace subtree only when both
 * the LD flag is on and the user holds the domain_name_reg_sqsp entitlement.
 * @param {boolean} isSquarespaceDomainsReseller
 * @param {Set<string>} [entitlements]
 * @returns {object}
 */
export function getDomainNameRegConfig(isSquarespaceDomainsReseller, entitlements = new Set()) {
  // eslint-disable-next-line max-len
  if (!isSquarespaceDomainsReseller || !entitlements.has(PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG_SQSP)) {
    return DOMAIN_NAME_REG_CONFIG;
  }
  return withSqspMessageKeys(DOMAIN_NAME_REG_CONFIG);
}
